import { useTheme } from '@emotion/react';
import { IconCheck } from '@hubble/icons';
import { spacing } from '@hubble/tokens/web';
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as React from 'react';
import { Flex } from '../Flex/index.js';
import { isNamedComponent } from '../PageLayout/utils.js';
import { Text } from '../Text/index.js';
import { Toggle } from '../Toggle/index.js';
import { validChildren } from '../visual-elements/VisualElementGroup/constants.js';
import { MenuLabelStyles, MenuStyles, MenuItemStyles, MenuGroupStyles, RadioGroupStyles, MenuSeparatorStyles } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';

const MenuRoot = MenuPrimitive.Root;
const MenuGroup = MenuGroupStyles;
const MenuPortal = MenuPrimitive.Portal;
const MenuRadioGroup = RadioGroupStyles;
const MenuSeparator = MenuSeparatorStyles;
const MenuTitle = React.forwardRef(({
  children,
  ...props
}, ref) => jsx(MenuPrimitive.Label, {
  asChild: true,
  ref: ref,
  ...props,
  children: jsx(MenuLabelStyles, {
    children: jsx(Text.Body, {
      size: "sm",
      bold: true,
      children: children
    })
  })
}));
const MenuLabel = React.forwardRef(({
  children,
  ...props
}, ref) => jsx(MenuPrimitive.Label, {
  asChild: true,
  ref: ref,
  ...props,
  children: jsx(MenuLabelStyles, {
    children: jsx(Text.Body, {
      size: "xs",
      bold: true,
      color: "inherit",
      children: children
    })
  })
}));
const MenuTrigger = React.forwardRef(({
  children,
  ...props
}, ref) => jsx(MenuPrimitive.Trigger, {
  asChild: true,
  ref: ref,
  ...props,
  children: children
}));
const MenuContent = React.forwardRef(({
  align = 'start',
  children,
  height,
  width,
  ...props
}, ref) => jsx(MenuPrimitive.Content, {
  ref: ref,
  align: align,
  ...props,
  sideOffset: 8,
  asChild: true,
  children: jsx(MenuStyles, {
    height: height,
    width: width,
    children: jsx(Flex, {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: spacing[4],
      children: children
    })
  })
}));
const MenuItemBase = React.forwardRef(({
  as = 'div',
  leftElement,
  label,
  description,
  data,
  metadata,
  rightElement,
  disabled,
  ...props
}, ref) => {
  const {
    colorScheme
  } = useTheme();
  let renderLeftElement;
  let isVisualElement;
  if (leftElement && React.isValidElement(leftElement)) {
    const isIcon = validChildren.find(name => {
      return isNamedComponent(leftElement, validChildren[name]);
    });
    if (isIcon) {
      isVisualElement = false;
      renderLeftElement = React.cloneElement(leftElement, {
        size: 'sm',
        display: 'block'
      });
    } else {
      isVisualElement = true;
      renderLeftElement = React.cloneElement(leftElement, {
        size: 'md'
      });
    }
  }
  return jsxs(MenuItemStyles, {
    ref: ref,
    as: as,
    ...props,
    children: [leftElement && jsx("div", {
      "data-visual-element": isVisualElement,
      children: renderLeftElement
    }), jsxs(Flex, {
      flexDirection: "column",
      justifyContent: "center",
      alignSelf: "stretch",
      flex: "1 0 0",
      children: [jsx(Text.Body, {
        size: "sm",
        as: "div",
        color: "inherit",
        children: label
      }), description && jsx(Text.Body, {
        size: "xs",
        as: "div",
        color: disabled ? 'inherit' : colorScheme.content.secondary,
        children: description
      })]
    }), data && jsxs(Flex, {
      flexDirection: "column",
      alignItems: "flex-end",
      alignSelf: "stretch",
      flex: "0",
      children: [jsx(Text.Body, {
        size: "sm",
        as: "div",
        color: "inherit",
        children: data
      }), metadata && jsx(Text.Body, {
        size: "xs",
        as: "div",
        color: disabled ? 'inherit' : colorScheme.content.secondary,
        children: metadata
      })]
    }), Boolean(rightElement) && rightElement]
  });
});
const MenuItem = React.forwardRef(({
  leftElement,
  label,
  description,
  data,
  metadata,
  rightElement,
  ...props
}, ref) => {
  return jsx(MenuPrimitive.Item, {
    asChild: true,
    ref: ref,
    ...props,
    children: jsx(MenuItemBase, {
      leftElement: leftElement,
      label: label,
      description: description,
      data: data,
      metadata: metadata,
      rightElement: rightElement,
      disabled: props.disabled
    })
  });
});
const MenuItemPresentational = React.forwardRef(({
  leftElement,
  label,
  description,
  data,
  metadata,
  rightElement,
  ...props
}, ref) => {
  return jsx(MenuItemBase, {
    leftElement: leftElement,
    label: label,
    description: description,
    data: data,
    metadata: metadata,
    rightElement: rightElement,
    disabled: props.disabled,
    ref: ref,
    "data-presentational": true
  });
});
const MenuItemLink = React.forwardRef(({
  as = 'a',
  leftElement,
  label,
  description,
  data,
  metadata,
  rightElement,
  ...props
}, ref) => {
  return jsx(MenuPrimitive.Item, {
    asChild: true,
    ref: ref,
    ...props,
    children: jsx(MenuItemBase, {
      as: as,
      leftElement: leftElement,
      label: label,
      description: description,
      data: data,
      metadata: metadata,
      rightElement: rightElement,
      ...props
    })
  });
});
const MenuCheckboxItem = React.forwardRef(({
  leftElement,
  label,
  description,
  data,
  metadata,
  checked,
  ...props
}, ref) => jsx(MenuPrimitive.CheckboxItem, {
  asChild: true,
  ref: ref,
  checked: checked,
  ...props,
  children: jsx(MenuItemBase, {
    leftElement: leftElement,
    label: label,
    description: description,
    data: data,
    metadata: metadata,
    rightElement: jsx(MenuPrimitive.ItemIndicator, {
      children: jsx(IconCheck, {
        display: "block",
        size: "sm"
      })
    }),
    disabled: props.disabled
  })
}));
const MenuToggleItem = React.forwardRef(({
  leftElement,
  label,
  description,
  data,
  metadata,
  checked,
  ['data-testid']: dataTestId,
  ...props
}, ref) => {
  const noop = () => {};
  return jsx(MenuPrimitive.CheckboxItem, {
    asChild: true,
    ref: ref,
    checked: checked,
    ...props,
    children: jsx(MenuItemBase, {
      leftElement: leftElement,
      label: label,
      description: description,
      data: data,
      metadata: metadata,
      rightElement: jsx(Toggle, {
        checked: typeof checked === 'boolean' && checked,
        disabled: props.disabled,
        "data-testid": dataTestId,
        onChange: noop
      }),
      disabled: props.disabled
    })
  });
});
const MenuRadioItem = React.forwardRef(({
  leftElement,
  label,
  description,
  data,
  metadata,
  ...props
}, ref) => jsx(MenuPrimitive.RadioItem, {
  asChild: true,
  ref: ref,
  ...props,
  children: jsx(MenuItemBase, {
    leftElement: leftElement,
    label: label,
    description: description,
    data: data,
    metadata: metadata,
    rightElement: jsx(MenuPrimitive.ItemIndicator, {
      children: jsx(IconCheck, {
        display: "block",
        size: "sm"
      })
    }),
    disabled: props.disabled
  })
}));
const Menu = Object.assign(MenuRoot, {
  CheckboxItem: MenuCheckboxItem,
  ToggleItem: MenuToggleItem,
  Content: MenuContent,
  Group: MenuGroup,
  Item: MenuItem,
  ItemLink: MenuItemLink,
  ItemPresentational: MenuItemPresentational,
  Label: MenuLabel,
  Portal: MenuPortal,
  RadioGroup: MenuRadioGroup,
  RadioItem: MenuRadioItem,
  Separator: MenuSeparator,
  Title: MenuTitle,
  Trigger: MenuTrigger
});
Menu.displayName = 'Menu';

export { Menu, MenuItemBase };
